import styled from 'styled-components';
import { Heading3Text } from '../../../components/Texts';
import TextInput from '../../../components/Inputs/Text';
import Button from '../../../components/Button';
import { useState } from 'react';
import { useLazyGetOnrampTransactionsQuery } from '../../../redux/api/authorized/onrampTransactions';
import LoadingSpinner from '../../../components/LoadingSpinner';

const Container = styled.div`
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  gap: 20px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.border?.primary || '#e0e0e0'};
  border-radius: 8px;
  background: ${({ theme }) => theme.background?.secondary || '#fafafa'};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border?.primary || '#e0e0e0'};
  background: ${({ theme }) => theme.background?.primary || '#fff'};
  font-weight: 600;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border?.primary || '#eee'};
  vertical-align: top;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Tr = styled.tr`
  &:hover {
    background: ${({ theme }) => theme.background?.secondary || 'rgba(0,0,0,0.02)'};
  }
`;

const ErrorBox = styled.div`
  padding: 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.status?.error?.background || '#f8d7da'};
  border: 1px solid ${({ theme }) => theme.status?.error?.primary || '#f5c6cb'};
  color: ${({ theme }) => theme.status?.error?.primary || '#721c24'};
`;

const JsonPre = styled.pre`
  background: ${({ theme }) => theme.background?.secondary || '#f8f9fa'};
  padding: 12px;
  border-radius: 8px;
  font-size: 12px;
  overflow: auto;
  max-height: 400px;
  margin: 0;
`;

const OnrampTransactions = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pageSize, setPageSize] = useState('1000');

  const [fetch, { data, error, isFetching }] = useLazyGetOnrampTransactionsQuery();

  const handleLoad = () => {
    fetch({
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      page_size: pageSize ? Number(pageSize) : undefined,
    });
  };

  const handleNextPage = () => {
    if (!data?.next_page_key) return;
    fetch({
      page_key: data.next_page_key,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      page_size: pageSize ? Number(pageSize) : undefined,
    });
  };

  const transactions = data?.transactions ?? (Array.isArray(data) ? data : null);
  const hasNextPage = data?.has_next_page && data?.next_page_key;

  return (
    <Container>
      <Heading3Text>Onramp transactions</Heading3Text>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary, #666)' }}>
        Account-level onramp transaction history. Optional date range and page size.
      </p>
      <InputContainer>
        <TextInput
          label="Start date (optional)"
          type="date"
          placeholder="YYYY-MM-DD"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <TextInput
          label="End date (optional)"
          type="date"
          placeholder="YYYY-MM-DD"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <TextInput
          label="Page size (optional)"
          type="number"
          placeholder="1000"
          value={pageSize}
          onChange={(e) => setPageSize(e.target.value)}
        />
        <Button onClick={handleLoad} loading={isFetching} disabled={isFetching}>
          {isFetching ? <LoadingSpinner /> : 'Load transactions'}
        </Button>
      </InputContainer>

      {error && (
        <ErrorBox>
          {error?.data?.message || error?.data?.error || error?.message || 'Failed to fetch onramp transactions'}
        </ErrorBox>
      )}

      {data && !error && (
        <>
          {transactions && transactions.length > 0 ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 14 }}>
                  {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
                  {data.has_next_page != null && ` • has_next_page: ${String(data.has_next_page)}`}
                </span>
                {hasNextPage && (
                  <Button variant="secondary" onClick={handleNextPage} loading={isFetching} disabled={isFetching}>
                    Next page
                  </Button>
                )}
              </div>
              <TableWrapper>
                <Table>
                  <thead>
                    <Tr as="tr">
                      {Object.keys(transactions[0]).map((key) => (
                        <Th key={key}>{key}</Th>
                      ))}
                    </Tr>
                  </thead>
                  <tbody>
                    {transactions.map((row, idx) => (
                      <Tr key={row.id ?? row.transaction_id ?? idx}>
                        {Object.keys(transactions[0]).map((key) => (
                          <Td key={key} title={typeof row[key] === 'object' ? JSON.stringify(row[key]) : String(row[key] ?? '')}>
                            {typeof row[key] === 'object' && row[key] !== null
                              ? JSON.stringify(row[key])
                              : String(row[key] ?? '')}
                          </Td>
                        ))}
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            </>
          ) : (
            <>
              <Heading3Text>Response</Heading3Text>
              <JsonPre>{JSON.stringify(data, null, 2)}</JsonPre>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default OnrampTransactions;
